'use client';
import { getTemplateImageRegenerateUrl, getTemplateImageUrl } from '@components/Modules/TemplatesModule/utils/urls';
import { useClientConfig } from '@providers/ClientConfigProvider';
import cn from '@utils/cn';
import Image from 'next/image';
import { useEffect, useRef, useState } from 'react';
import { DESIGN_SIZE } from './constants';

type TemplateImageClientProps = {
  hash: string;
  name?: string;
  isHidden?: boolean;
  designSize?: DESIGN_SIZE;
  onLoad?: () => void;
};

export default function TemplateImageClient({
  hash,
  name,
  isHidden,
  designSize = DESIGN_SIZE.DEFAULT,
  onLoad,
}: TemplateImageClientProps) {
  const config = useClientConfig();
  const [numberOfRetries, setNumberOfRetries] = useState(0);
  const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout | null>(null);
  const imgRef = useRef<HTMLImageElement>(null);

  function onError() {
    if (numberOfRetries >= 10) {
      return;
    }

    if (numberOfRetries === 0) {
      return setNumberOfRetries((count) => count + 1);
    }

    setTimeoutId(
      setTimeout(() => {
        setNumberOfRetries((count) => count + 1);
      }, 500),
    );
  }

  // cleanup running timeouts
  useEffect(() => {
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [timeoutId]);

  useEffect(() => {
    // Handle cases where the image is loaded before react is ready
    if (imgRef.current && imgRef.current.complete && onLoad) {
      onLoad();
    }
  }, [onLoad]);

  const src =
    numberOfRetries === 1
      ? getTemplateImageRegenerateUrl(hash, config.RESIZE_URL)
      : getTemplateImageUrl(hash, config.CDN_URL);

  return (
    <Image
      ref={imgRef}
      src={src}
      alt={name ?? 'template'}
      width={0}
      height={0}
      sizes="100vw"
      className={cn('block h-auto w-auto select-none object-contain', 'transition-opacity duration-500', {
        'opacity-0': isHidden,
        'max-h-[336px] lg:max-h-[556px]': designSize === DESIGN_SIZE.DEFAULT,
        'max-h-[500px]': designSize === DESIGN_SIZE.HERO,
        'max-h-[178px] md:max-h-[300px]': designSize === DESIGN_SIZE.SLIDER,
      })}
      onError={onError}
      onLoad={onLoad}
      loading="lazy"
      unoptimized
    />
  );
}
